<script>
import SpellCheckedText from "@/components/SpellCheckedText.vue";
import PhotoBox from "../../PhotoBox.vue";

export default {
    name: "LPTemp5",
    components: {
        SpellCheckedText,
        PhotoBox
    },
    props: ["proposal"],
    data() {
        return {
            footerImage: ""
        };
    },
    computed: {
        project() {
            return this.proposal.projects && this.proposal.projects.length > 0
                ? this.proposal.projects[0]
                : null;
        }
    },
    mounted() {
        this.generateGradients();
    },
    methods: {
        generateGradients() {
            const fCanvas = document.createElement('canvas');
            fCanvas.width = 800;
            fCanvas.height = 300;
            const fCtx = fCanvas.getContext('2d');
            const fGrad = fCtx.createLinearGradient(0, 300, 0, 0);
            fGrad.addColorStop(0, 'rgba(0, 0, 0, 0.8)');
            fGrad.addColorStop(1, 'rgba(0, 0, 0, 0)');
            fCtx.fillStyle = fGrad;
            fCtx.fillRect(0, 0, 800, 300);
            this.footerImage = fCanvas.toDataURL();
        }
    }
};
</script>

<template>
  <div class="main-container" :style="{
    '--header-color': proposal.style.colorScheme.header,
    '--body-color': proposal.style.colorScheme.body,
    '--footer-color': proposal.style.colorScheme.footer,
    '--heading-color': proposal.style.heading.color,
    '--heading-size': proposal.style.heading.size + 'px',
    '--heading-font': proposal.style.heading.font,
    '--heading-weight': proposal.style.heading.bold ? 'bold' : 'normal',
    '--heading-style': proposal.style.heading.italic ? 'italic' : 'normal',
    '--heading-decoration': proposal.style.heading.underline ? 'underline' : 'none',
    '--label-color': proposal.style.label.color,
    '--label-size': proposal.style.label.size + 'px',
    '--label-font': proposal.style.label.font,
    '--label-weight': proposal.style.label.bold ? 'bold' : 'normal',
    '--label-style': proposal.style.label.italic ? 'italic' : 'normal',
    '--label-decoration': proposal.style.label.underline ? 'underline' : 'none',
    '--text-color': proposal.style.text.color,
    '--text-size': proposal.style.text.size + 'px',
    '--text-font': proposal.style.text.font,
    '--text-weight': proposal.style.text.bold ? 'bold' : 'normal',
    '--text-style': proposal.style.text.italic ? 'italic' : 'normal',
    '--text-decoration': proposal.style.text.underline ? 'underline' : 'none',
  }">
    <div class="lastPage">
      <!-- Top Spacer -->
      <div class="top-spacer"></div>

      <!-- Image Section -->
      <div class="image-sec">
        <div class="image-container">
          <PhotoBox v-if="project" class="photobox" :project="project" height="560.94px"
            :bgColor="proposal.style.colorScheme && proposal.style.colorScheme.body" />
          <div v-else class="placeholder-image" />

          <div class="footer-overlay" v-if="project && footerImage">
            <img :src="footerImage" class="w-full h-full" />
          </div>

          <div class="image-overlay" v-if="project">
            <p class="project-name">
              <SpellCheckedText :value="project.projectName || ''" @input="project.projectName = $event" />
            </p>
            <p class="project-location">
              <SpellCheckedText :value="project.city || ''" @input="project.city = $event" />,
              <SpellCheckedText :value="project.stateProvince || ''" @input="project.stateProvince = $event" />
            </p>
          </div>
        </div>
      </div>

      <!-- Bottom Content Section -->
      <div class="bottom-sec">
        <div class="logo-wrapper photobox" v-if="proposal.logo">
          <PhotoBox :project="proposal" type="logo" bgColor="transparent" objectFit="contain" />
        </div>

        <div class="company-details">
          <p class="comp-name">
            <SpellCheckedText :value="proposal.name || ''" @input="proposal.name = $event" />
          </p>
          <p>
            <SpellCheckedText :value="proposal.address || ''" @input="proposal.address = $event" />
          </p>
          <p>
            <SpellCheckedText :value="proposal.city || ''" @input="proposal.city = $event" />,
            <SpellCheckedText :value="proposal.stateProvince || ''" @input="proposal.stateProvince = $event" />
          </p>
          <p>
            <SpellCheckedText :value="proposal.contactNo || ''" @input="proposal.contactNo = $event" />
          </p>
          <p>
            <SpellCheckedText :value="proposal.email || ''" @input="proposal.email = $event" />
          </p>
          <p>
            <SpellCheckedText :value="proposal.website || ''" @input="proposal.website = $event" />
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.main-container {
  display: block;
  width: 211mm;
  height: 296.83mm;
  min-height: 296.83mm;
  position: relative;
  box-sizing: border-box;
  overflow: hidden;

  &:hover {
    .photobox :deep(.photo-overlay) {
      opacity: 1;
    }

    .photobox :deep(img) {
      opacity: 0.9;
    }
  }

  .lastPage {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;

    .top-spacer {
      height: 25%;
      background: var(--header-color);
    }

    .image-sec {
      position: relative;
      width: 100%;
      height: 50%;

      .image-container {
        position: relative;
        width: 100%;
        height: 100%;

        .placeholder-image {
          width: 100%;
          height: 100%;
          background: var(--body-color);
        }

        .footer-overlay {
          position: absolute;
          bottom: 0;
          left: 0;
          width: 100%;
          z-index: 2;
          pointer-events: none;
        }

        .image-overlay {
          position: absolute;
          bottom: 0;
          left: 0;
          z-index: 50;
          padding: 15px 30px;
          color: white;
          text-align: left;
          width: 100%;

          .project-name {
            font-size: 16px;
            font-weight: 800;
            font-style: italic;
            margin: 0;
          }

          .project-location {
            font-size: 16px;
            font-weight: 800;
            font-style: italic;
            margin: 0;
          }
        }
      }
    }

    .bottom-sec {
      height: 25%;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: end;
      padding: 20px;
      background: var(--footer-color);


      .logo-wrapper {
        margin-bottom: 5px;
        width: 250px;
        height: 80px;
      }

      .company-details {
        text-align: center;
        line-height: 1.2;

        p {
          color: var(--text-color);
          font-size: var(--text-size);
          font-family: var(--text-font);
          font-weight: var(--text-weight);
          font-style: var(--text-style);
          text-decoration: var(--text-decoration);
          margin-bottom: 5px;

          &.comp-name {
            color: var(--label-color);
            font-size: var(--label-size);
            font-family: var(--label-font);
            font-weight: var(--label-weight);
            font-style: var(--label-style);
            text-decoration: var(--label-decoration);
            margin-bottom: 5px;
          }
        }
      }
    }
  }
}
</style>
